import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getBackendUrl } from '../utils/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Calendar, MapPin, Users, Gift, CheckCircle, Loader2 } from 'lucide-react';

const API = getBackendUrl();

const InviteRedeem = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const disclaimerRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [invite, setInvite] = useState(null);
  const [disclaimer, setDisclaimer] = useState(null);
  const [success, setSuccess] = useState(false);
  const [ticketNumber, setTicketNumber] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    date_of_birth: '',
    address: '',
    postcode: '',
    emergency_contact_name: '',
    emergency_contact_phone: ''
  });
  
  // Disclaimer state
  const [disclaimerScrolled, setDisclaimerScrolled] = useState(false);
  const [acceptedDisclaimer, setAcceptedDisclaimer] = useState(false);
  
  // Signature state
  const [signatureType, setSignatureType] = useState('type');
  const [typedSignature, setTypedSignature] = useState('');
  const [drawnSignature, setDrawnSignature] = useState(null);
  const signatureCanvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  
  // Parent/Guardian state (for under 18)
  const [parentGuardian, setParentGuardian] = useState({
    name: '',
    relationship: 'Parent',
    phone: '',
    signatureType: 'type',
    typedSignature: '',
    drawnSignature: null
  });
  const parentSignatureCanvasRef = useRef(null);
  const [isParentDrawing, setIsParentDrawing] = useState(false);
  
  // Calculate age from DOB
  const calculateAge = (dob) => {
    if (!dob) return null;
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };
  
  const participantAge = calculateAge(formData.date_of_birth);
  const isMinor = participantAge !== null && participantAge < 18;

  // Signature drawing functions
  const startDrawing = (e, canvasRef, setDrawingState) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    ctx.beginPath();
    ctx.moveTo(x, y);
    setDrawingState(true);
  };

  const draw = (e, canvasRef, isCurrentlyDrawing) => {
    if (!isCurrentlyDrawing) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#c5bfa9';
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = (canvasRef, setDrawingState, setSignatureData) => {
    setDrawingState(false);
    const canvas = canvasRef.current;
    if (canvas) {
      setSignatureData(canvas.toDataURL('image/png'));
    }
  };

  const clearCanvas = (canvasRef, setSignatureData) => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      setSignatureData(null);
    }
  };

  // Check scroll position for disclaimer
  const checkScrollPosition = useCallback(() => {
    if (disclaimerRef.current) {
      const el = disclaimerRef.current;
      const { scrollTop, scrollHeight, clientHeight } = el;
      const isAtBottom = Math.ceil(scrollTop + clientHeight) >= scrollHeight - 5;
      const noScrollNeeded = scrollHeight <= clientHeight + 5;
      if (isAtBottom || noScrollNeeded) {
        setDisclaimerScrolled(true);
      }
    }
  }, []);

  useEffect(() => {
    fetchInvite();
    fetchDisclaimer();
  }, [token]);

  useEffect(() => {
    if (disclaimer) {
      setTimeout(checkScrollPosition, 300);
    }
  }, [disclaimer, checkScrollPosition]);

  const fetchInvite = async () => {
    try {
      const response = await fetch(`${API}/booking/invite/${token}`);
      const data = await response.json();
      
      if (response.ok && data.success) {
        setInvite(data.invite);
      } else {
        setError(data.detail || 'Invalid or expired invite');
      }
    } catch (err) {
      setError('Failed to load invite details');
    } finally {
      setLoading(false);
    }
  };

  const fetchDisclaimer = async () => {
    try {
      const res = await fetch(`${API}/booking/disclaimer`);
      const data = await res.json();
      if (data.success && data.disclaimer) {
        setDisclaimer(data.disclaimer);
      }
    } catch (err) {
      console.error('Error fetching disclaimer:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    const missingFields = [];
    if (!formData.name) missingFields.push('Full Name');
    if (!formData.phone) missingFields.push('Phone Number');
    if (!formData.date_of_birth) missingFields.push('Date of Birth');
    if (!formData.address) missingFields.push('Address');
    if (!formData.postcode) missingFields.push('Postcode');
    if (!formData.emergency_contact_name) missingFields.push('Emergency Contact Name');
    if (!formData.emergency_contact_phone) missingFields.push('Emergency Contact Phone');
    
    if (missingFields.length > 0) {
      toast.error(`Please fill in: ${missingFields.join(', ')}`);
      return;
    }
    
    if (!acceptedDisclaimer) {
      toast.error('You must accept the disclaimer to continue');
      return;
    }

    // Validate signature
    const hasParticipantSignature = signatureType === 'type' 
      ? typedSignature.trim() 
      : drawnSignature;
    
    if (!isMinor && !hasParticipantSignature) {
      toast.error('Please provide your signature');
      return;
    }

    // Validate parent/guardian for minors
    if (isMinor) {
      if (!parentGuardian.name || !parentGuardian.phone) {
        toast.error('Parent/Guardian details are required for participants under 18');
        return;
      }
      const hasParentSignature = parentGuardian.signatureType === 'type'
        ? parentGuardian.typedSignature.trim()
        : parentGuardian.drawnSignature;
      if (!hasParentSignature) {
        toast.error('Parent/Guardian signature is required');
        return;
      }
    }
    
    setSubmitting(true);
    try {
      // Build signature data
      const signatureData = {
        signature_type: signatureType,
        signature_data: signatureType === 'type' ? typedSignature : drawnSignature,
        signature_date: new Date().toISOString().split('T')[0],
        is_minor: isMinor
      };

      if (isMinor) {
        signatureData.parent_guardian_name = parentGuardian.name;
        signatureData.parent_guardian_relationship = parentGuardian.relationship;
        signatureData.parent_guardian_phone = parentGuardian.phone;
        signatureData.parent_guardian_signature_type = parentGuardian.signatureType;
        signatureData.parent_guardian_signature_data = parentGuardian.signatureType === 'type' 
          ? parentGuardian.typedSignature 
          : parentGuardian.drawnSignature;
      }

      const response = await fetch(`${API}/booking/invite/${token}/redeem`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          phone: formData.phone,
          date_of_birth: formData.date_of_birth,
          address: formData.address,
          postcode: formData.postcode,
          emergency_contact_name: formData.emergency_contact_name,
          emergency_contact_phone: formData.emergency_contact_phone,
          signature: signatureData,
          disclaimer_accepted: acceptedDisclaimer
        })
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        setSuccess(true);
        setTicketNumber(data.ticket_number);
        toast.success('Registration complete!');
      } else {
        toast.error(data.detail || 'Failed to complete registration');
      }
    } catch (err) {
      toast.error('Failed to complete registration');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0d1009] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#4a5d42] mx-auto mb-4" />
          <p className="text-[#8b9a7e]">Loading invite...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0d1009] flex items-center justify-center p-4">
        <Card className="max-w-md w-full bg-[#1a1f16] border-red-800/50">
          <CardContent className="pt-8 text-center">
            <div className="w-16 h-16 rounded-full bg-red-900/30 flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">❌</span>
            </div>
            <h2 className="text-xl font-bold text-red-400 mb-2">Invite Error</h2>
            <p className="text-[#8b9a7e] mb-6">{error}</p>
            <Button 
              onClick={() => navigate('/')}
              className="bg-[#4a5d42] hover:bg-[#5a6f4e]"
            >
              Return to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-[#0d1009] flex items-center justify-center p-4">
        <Card className="max-w-md w-full bg-[#1a1f16] border-[#4a5d42]">
          <CardContent className="pt-8 text-center">
            <div className="w-16 h-16 rounded-full bg-green-900/30 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
            <h2 className="text-2xl font-bold text-[#c5bfa9] mb-2">Registration Complete!</h2>
            <p className="text-[#8b9a7e] mb-4">Your spot is confirmed for {invite?.event_name}</p>
            <div className="bg-[#2d3728] rounded-lg p-4 mb-6">
              <p className="text-sm text-[#8b9a7e] mb-1">Your Ticket Number</p>
              <p className="text-2xl font-bold text-[#c5bfa9] tracking-wider">{ticketNumber}</p>
            </div>
            <p className="text-sm text-[#8b9a7e] mb-6">
              A confirmation email with your ticket has been sent to {invite?.email}
            </p>
            <Button 
              onClick={() => navigate('/')}
              className="bg-[#4a5d42] hover:bg-[#5a6f4e]"
            >
              Return to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0d1009] py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-full bg-[#4a5d42]/30 flex items-center justify-center mx-auto mb-4">
            <Gift className="w-8 h-8 text-[#c5bfa9]" />
          </div>
          <h1 className="text-3xl font-bold text-[#c5bfa9] mb-2">You're Invited!</h1>
          <p className="text-[#8b9a7e]">Complete your registration for a complimentary spot</p>
        </div>

        {/* Event Details Card */}
        <Card className="bg-[#1a1f16] border-[#3d4a35] mb-6">
          <CardHeader className="border-b border-[#3d4a35]">
            <CardTitle className="text-[#c5bfa9]">{invite?.event_name}</CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {invite?.event_date && (
                <div className="flex items-center gap-3">
                  <Calendar className="w-5 h-5 text-[#8b9a7e]" />
                  <div>
                    <p className="text-xs text-[#6b7a5e]">Date</p>
                    <p className="text-[#c5bfa9]">{invite.event_date}</p>
                  </div>
                </div>
              )}
              {invite?.event_location && (
                <div className="flex items-center gap-3">
                  <MapPin className="w-5 h-5 text-[#8b9a7e]" />
                  <div>
                    <p className="text-xs text-[#6b7a5e]">Location</p>
                    <p className="text-[#c5bfa9]">{invite.event_location}</p>
                  </div>
                </div>
              )}
              <div className="flex items-center gap-3">
                <Users className="w-5 h-5 text-[#8b9a7e]" />
                <div>
                  <p className="text-xs text-[#6b7a5e]">Team</p>
                  <p className="text-[#c5bfa9]">{invite?.team_name}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Gift className="w-5 h-5 text-green-500" />
                <div>
                  <p className="text-xs text-[#6b7a5e]">Price</p>
                  <p className="text-green-400 font-bold">FREE (Complimentary)</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Registration Form */}
        <Card className="bg-[#1a1f16] border-[#3d4a35]">
          <CardHeader className="border-b border-[#3d4a35]">
            <CardTitle className="text-[#c5bfa9]">Complete Your Registration</CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Email (read-only) */}
              <div>
                <Label className="text-[#8b9a7e]">Email</Label>
                <Input
                  type="email"
                  value={invite?.email || ''}
                  disabled
                  className="bg-[#2d3728] border-[#3d4a35] text-[#8b9a7e]"
                />
              </div>
              
              {/* Full Name */}
              <div>
                <Label className="text-[#8b9a7e]">Full Name *</Label>
                <Input
                  type="text"
                  data-testid="invite-name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="bg-[#1a1f16] border-[#3d4a35] text-[#c5bfa9]"
                  placeholder="Your full name"
                />
              </div>
              
              {/* Phone */}
              <div>
                <Label className="text-[#8b9a7e]">Phone Number *</Label>
                <Input
                  type="tel"
                  data-testid="invite-phone-input"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  required
                  className="bg-[#1a1f16] border-[#3d4a35] text-[#c5bfa9]"
                  placeholder="07XXX XXXXXX"
                />
              </div>

              {/* Date of Birth */}
              <div>
                <Label className="text-[#8b9a7e]">Date of Birth *</Label>
                <Input
                  type="date"
                  data-testid="invite-dob-input"
                  value={formData.date_of_birth}
                  onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
                  required
                  max={new Date().toISOString().split('T')[0]}
                  className="bg-[#1a1f16] border-[#3d4a35] text-[#c5bfa9]"
                />
                {participantAge !== null && (
                  <p className={`text-xs mt-1 ${isMinor ? 'text-amber-500' : 'text-[#6b7a5e]'}`}>
                    Age: {participantAge} years {isMinor && '(Parent/Guardian signature required)'}
                  </p>
                )}
              </div>

              {/* Address */}
              <div>
                <Label className="text-[#8b9a7e]">Address *</Label>
                <Input
                  type="text"
                  data-testid="invite-address-input"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  required
                  className="bg-[#1a1f16] border-[#3d4a35] text-[#c5bfa9]"
                  placeholder="Enter your full address"
                />
              </div>

              {/* Postcode */}
              <div>
                <Label className="text-[#8b9a7e]">Postcode *</Label>
                <Input
                  type="text"
                  data-testid="invite-postcode-input"
                  value={formData.postcode}
                  onChange={(e) => setFormData({ ...formData, postcode: e.target.value.toUpperCase() })}
                  required
                  className="bg-[#1a1f16] border-[#3d4a35] text-[#c5bfa9] uppercase"
                  placeholder="XX00 0XX"
                />
              </div>
              
              {/* Emergency Contact */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-[#8b9a7e]">Emergency Contact Name *</Label>
                  <Input
                    type="text"
                    data-testid="invite-emergency-name-input"
                    value={formData.emergency_contact_name}
                    onChange={(e) => setFormData({ ...formData, emergency_contact_name: e.target.value })}
                    required
                    className="bg-[#1a1f16] border-[#3d4a35] text-[#c5bfa9]"
                    placeholder="Emergency contact name"
                  />
                </div>
                <div>
                  <Label className="text-[#8b9a7e]">Emergency Contact Phone *</Label>
                  <Input
                    type="tel"
                    data-testid="invite-emergency-phone-input"
                    value={formData.emergency_contact_phone}
                    onChange={(e) => setFormData({ ...formData, emergency_contact_phone: e.target.value })}
                    required
                    className="bg-[#1a1f16] border-[#3d4a35] text-[#c5bfa9]"
                    placeholder="Their phone number"
                  />
                </div>
              </div>
              
              {/* Disclaimer */}
              {disclaimer && (
                <div className="bg-[#2d3728] rounded-lg p-4 border border-[#3d4a35]">
                  <h3 className="text-[#c5bfa9] font-semibold mb-3">{disclaimer.title}</h3>
                  <div 
                    ref={disclaimerRef}
                    className="text-[#8b9a7e] text-sm mb-4 max-h-48 overflow-y-auto prose prose-sm prose-invert border border-[#3d4a35] rounded p-3 bg-[#1a1f16]"
                    dangerouslySetInnerHTML={{ __html: disclaimer.content }}
                    onScroll={checkScrollPosition}
                  />
                  
                  {!disclaimerScrolled && (
                    <p className="text-amber-500 text-xs mb-3 flex items-center gap-2">
                      <svg className="w-4 h-4 animate-bounce" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                      </svg>
                      Please scroll through the entire disclaimer to enable acceptance
                    </p>
                  )}
                  
                  <label className={`flex items-start gap-3 ${disclaimerScrolled ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}>
                    <input
                      type="checkbox"
                      data-testid="invite-disclaimer-checkbox"
                      checked={acceptedDisclaimer}
                      onChange={(e) => disclaimerScrolled && setAcceptedDisclaimer(e.target.checked)}
                      disabled={!disclaimerScrolled}
                      className="mt-1 w-5 h-5 rounded border-[#3d4a35] bg-[#1a1f16] text-[#4a5d42] focus:ring-[#4a5d42] disabled:opacity-50"
                    />
                    <span className={`text-sm ${disclaimerScrolled ? 'text-[#c5bfa9]' : 'text-[#6b7a5e]'}`}>
                      I have read, understood, and agree to the above disclaimer/waiver. 
                      I confirm that all information provided is accurate.
                    </span>
                  </label>
                </div>
              )}

              {/* Signature Section */}
              {acceptedDisclaimer && (
                <div className="bg-[#2d3728] rounded-lg p-4 border border-[#3d4a35] space-y-4">
                  <h3 className="text-[#c5bfa9] font-semibold">
                    {isMinor ? 'Parent/Guardian Signature' : 'Participant Signature'}
                  </h3>
                  <p className="text-[#8b9a7e] text-sm">
                    {isMinor 
                      ? 'As the participant is under 18, a parent or legal guardian must sign below.'
                      : 'Please sign below to confirm your agreement to the disclaimer.'}
                  </p>

                  {/* Parent/Guardian Details (if minor) */}
                  {isMinor && (
                    <div className="space-y-4 border-b border-[#3d4a35] pb-4">
                      <div>
                        <Label className="text-[#8b9a7e]">Parent/Guardian Full Name *</Label>
                        <Input
                          type="text"
                          value={parentGuardian.name}
                          onChange={(e) => setParentGuardian(prev => ({ ...prev, name: e.target.value }))}
                          required
                          className="bg-[#1a1f16] border-[#3d4a35] text-[#c5bfa9]"
                          placeholder="Enter parent/guardian full name"
                        />
                      </div>
                      
                      <div>
                        <Label className="text-[#8b9a7e]">Relationship to Participant *</Label>
                        <select
                          value={parentGuardian.relationship}
                          onChange={(e) => setParentGuardian(prev => ({ ...prev, relationship: e.target.value }))}
                          className="w-full px-3 py-2 bg-[#1a1f16] border border-[#3d4a35] rounded-md text-[#c5bfa9]"
                        >
                          <option value="Parent">Parent</option>
                          <option value="Legal Guardian">Legal Guardian</option>
                        </select>
                      </div>
                      
                      <div>
                        <Label className="text-[#8b9a7e]">Parent/Guardian Contact Phone *</Label>
                        <Input
                          type="tel"
                          value={parentGuardian.phone}
                          onChange={(e) => setParentGuardian(prev => ({ ...prev, phone: e.target.value }))}
                          required
                          className="bg-[#1a1f16] border-[#3d4a35] text-[#c5bfa9]"
                          placeholder="07XXX XXXXXX"
                        />
                      </div>
                    </div>
                  )}

                  {/* Signature Type Toggle */}
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => isMinor 
                        ? setParentGuardian(prev => ({ ...prev, signatureType: 'type' }))
                        : setSignatureType('type')
                      }
                      className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
                        (isMinor ? parentGuardian.signatureType : signatureType) === 'type'
                          ? 'bg-[#4a5d42] text-white border border-[#5a6f4e]'
                          : 'bg-[#1a1f16] text-[#8b9a7e] border border-[#3d4a35] hover:border-[#4a5d42]'
                      }`}
                    >
                      Type Signature
                    </button>
                    <button
                      type="button"
                      onClick={() => isMinor
                        ? setParentGuardian(prev => ({ ...prev, signatureType: 'draw' }))
                        : setSignatureType('draw')
                      }
                      className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
                        (isMinor ? parentGuardian.signatureType : signatureType) === 'draw'
                          ? 'bg-[#4a5d42] text-white border border-[#5a6f4e]'
                          : 'bg-[#1a1f16] text-[#8b9a7e] border border-[#3d4a35] hover:border-[#4a5d42]'
                      }`}
                    >
                      Draw Signature
                    </button>
                  </div>

                  {/* Typed Signature */}
                  {(isMinor ? parentGuardian.signatureType : signatureType) === 'type' && (
                    <div>
                      <Label className="text-[#8b9a7e]">
                        {isMinor ? 'Parent/Guardian Signature (Type your full name) *' : 'Signature (Type your full name) *'}
                      </Label>
                      <Input
                        type="text"
                        value={isMinor ? parentGuardian.typedSignature : typedSignature}
                        onChange={(e) => isMinor
                          ? setParentGuardian(prev => ({ ...prev, typedSignature: e.target.value }))
                          : setTypedSignature(e.target.value)
                        }
                        className="bg-[#1a1f16] border-[#3d4a35] text-[#c5bfa9] italic text-xl"
                        placeholder="Type your full legal name"
                        style={{ fontFamily: 'cursive, serif' }}
                      />
                    </div>
                  )}

                  {/* Drawn Signature */}
                  {(isMinor ? parentGuardian.signatureType : signatureType) === 'draw' && (
                    <div>
                      <Label className="text-[#8b9a7e]">
                        {isMinor ? 'Parent/Guardian Signature (Draw below) *' : 'Signature (Draw below) *'}
                      </Label>
                      <div className="relative">
                        <canvas
                          ref={isMinor ? parentSignatureCanvasRef : signatureCanvasRef}
                          width={400}
                          height={150}
                          className="w-full bg-[#1a1f16] border border-[#3d4a35] rounded-lg cursor-crosshair touch-none"
                          onMouseDown={(e) => startDrawing(e, isMinor ? parentSignatureCanvasRef : signatureCanvasRef, isMinor ? setIsParentDrawing : setIsDrawing)}
                          onMouseMove={(e) => draw(e, isMinor ? parentSignatureCanvasRef : signatureCanvasRef, isMinor ? isParentDrawing : isDrawing)}
                          onMouseUp={() => stopDrawing(isMinor ? parentSignatureCanvasRef : signatureCanvasRef, isMinor ? setIsParentDrawing : setIsDrawing, isMinor ? (data) => setParentGuardian(prev => ({ ...prev, drawnSignature: data })) : setDrawnSignature)}
                          onMouseLeave={() => stopDrawing(isMinor ? parentSignatureCanvasRef : signatureCanvasRef, isMinor ? setIsParentDrawing : setIsDrawing, isMinor ? (data) => setParentGuardian(prev => ({ ...prev, drawnSignature: data })) : setDrawnSignature)}
                          onTouchStart={(e) => startDrawing(e, isMinor ? parentSignatureCanvasRef : signatureCanvasRef, isMinor ? setIsParentDrawing : setIsDrawing)}
                          onTouchMove={(e) => draw(e, isMinor ? parentSignatureCanvasRef : signatureCanvasRef, isMinor ? isParentDrawing : isDrawing)}
                          onTouchEnd={() => stopDrawing(isMinor ? parentSignatureCanvasRef : signatureCanvasRef, isMinor ? setIsParentDrawing : setIsDrawing, isMinor ? (data) => setParentGuardian(prev => ({ ...prev, drawnSignature: data })) : setDrawnSignature)}
                        />
                        <button
                          type="button"
                          onClick={() => clearCanvas(isMinor ? parentSignatureCanvasRef : signatureCanvasRef, isMinor ? (data) => setParentGuardian(prev => ({ ...prev, drawnSignature: data })) : setDrawnSignature)}
                          className="absolute top-2 right-2 px-3 py-1 text-xs bg-[#3d4a35] text-[#8b9a7e] rounded hover:bg-[#4a5d42] transition-colors"
                        >
                          Clear
                        </button>
                      </div>
                      <p className="text-[#6b7a5e] text-xs mt-1">Use your mouse or finger to sign above</p>
                    </div>
                  )}

                  {/* Signature Date */}
                  <div className="flex items-center justify-between pt-2 border-t border-[#3d4a35]">
                    <span className="text-[#8b9a7e] text-sm">Date of Signature:</span>
                    <span className="text-[#c5bfa9] text-sm">{new Date().toLocaleDateString('en-GB')}</span>
                  </div>
                </div>
              )}
              
              <Button
                type="submit"
                data-testid="invite-submit-btn"
                disabled={submitting || !acceptedDisclaimer}
                className="w-full bg-[#4a5d42] hover:bg-[#5a6f4e] text-white py-3"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  'Complete Registration'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default InviteRedeem;
